import React, { useState } from 'react';
import { authService, AuthError } from '../../services/authService';
import { trackEvent, AnalyticsEvent } from '../../services/analyticsService';

interface AuthPageProps {
  onAuthSuccess: () => void;
  onBack?: () => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ onAuthSuccess, onBack }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [agreedToPolicies, setAgreedToPolicies] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Validate legal agreements for sign up
        if (!agreedToPolicies) {
          setError('Please accept the terms and policies to continue.');
          setLoading(false);
          return;
        }

        // Validate age (must be 13+)
        if (dateOfBirth) {
          const birthDate = new Date(dateOfBirth);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
          
          if (actualAge < 13) {
            setError('You must be at least 13 years old to use AwakenArc.');
            setLoading(false);
            return;
          }
        }

        await authService.signUp(email, password, displayName || 'Hunter', dateOfBirth, {
          termsOfService: agreedToPolicies,
          privacyPolicy: agreedToPolicies,
          refundPolicy: agreedToPolicies
        });
        trackEvent(AnalyticsEvent.USER_SIGN_UP, { method: 'email' });
      } else {
        await authService.signIn(email, password);
        trackEvent(AnalyticsEvent.USER_LOGIN, { method: 'email' });
      }
      onAuthSuccess();
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await authService.signInWithGoogle();
      trackEvent(isSignUp ? AnalyticsEvent.USER_SIGN_UP : AnalyticsEvent.USER_LOGIN, {
        method: 'google'
      });
      onAuthSuccess();
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
    } finally {
      setLoading(false);
    }
  };


  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Prevent multiple clicks
    if (resetPasswordLoading) {
      return;
    }

    setError('');
    setResetPasswordLoading(true);

    try {
      await authService.resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 sm:p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute h-px bg-gradient-to-r from-transparent via-system-blue to-transparent"
              style={{
                top: `${i * 5}%`,
                left: 0,
                right: 0,
                animation: `pulse ${3 + i * 0.2}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Auth Card */}
      <div className="relative z-10 w-full max-w-md animate-level-up">
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="p-4 sm:p-6 md:p-8">
            {/* Back Button */}
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-system-blue hover:text-system-purple transition-colors mb-4 font-rajdhani"
              >
                <span>←</span>
                <span>Back to Home</span>
              </button>
            )}
            
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="mb-3 sm:mb-4 flex justify-center">
                <img 
                  src="/logo/logo.png" 
                  alt="AwakenArc Logo" 
                  className="h-16 sm:h-20 md:h-24 w-auto object-contain animate-float"
                />
              </div>
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
                <span className="font-orbitron text-xs sm:text-sm tracking-wider text-system-blue uppercase">
                  ［ System Login ］
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-3xl font-black font-orbitron glow-text mb-2">
                AWAKEN<span className="text-system-blue">ARC</span>
              </h1>
              <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
                {isSignUp ? 'Register as a new Hunter' : 'Welcome back, Hunter'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-red-500/20 border-2 border-red-500/50 rounded text-red-400 text-xs sm:text-sm font-rajdhani">
                ⚠️ {error}
              </div>
            )}

            {/* Success Message */}
            {resetEmailSent && (
              <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-green-500/20 border-2 border-green-500/50 rounded text-green-400 text-xs sm:text-sm font-rajdhani">
                ✓ Password reset email sent! Check your inbox.
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {isSignUp && (
                <div>
                  <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-1.5 sm:mb-2">
                    Hunter Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white font-rajdhani focus:outline-none focus:border-system-blue transition-colors"
                    placeholder="Enter your name"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-1.5 sm:mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white font-rajdhani focus:outline-none focus:border-system-blue transition-colors"
                  placeholder="hunter@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-1.5 sm:mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white font-rajdhani focus:outline-none focus:border-system-blue transition-colors"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              {isSignUp && (
                <div>
                  <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-1.5 sm:mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full bg-system-darker border-2 border-system-blue/30 rounded px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white font-rajdhani focus:outline-none focus:border-system-blue transition-colors"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 mt-1">Required for age verification (13+ years)</p>
                </div>
              )}

              {isSignUp && (
                <div className="pt-2">
                  <label className="flex items-start gap-2 text-xs sm:text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToPolicies}
                      onChange={(e) => setAgreedToPolicies(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-system-blue bg-system-darker border-system-blue/30 rounded focus:ring-system-blue focus:ring-2"
                      required
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" className="text-system-blue hover:underline">
                        Terms of Service
                      </a>
                      {', '}
                      <a href="/privacy" target="_blank" className="text-system-blue hover:underline">
                        Privacy Policy
                      </a>
                      {', and '}
                      <a href="/refund" target="_blank" className="text-system-blue hover:underline">
                        Refund Policy
                      </a>
                      <span className="text-red-400"> *</span>
                    </span>
                  </label>
                </div>
              )}

              {!isSignUp && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetPasswordLoading}
                    className="text-xs sm:text-sm text-system-blue hover:text-system-purple font-rajdhani transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetPasswordLoading ? 'Sending email...' : 'Forgot password?'}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full relative group px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(to bottom, rgba(30, 64, 175, 0.9), rgba(30, 58, 138, 0.9))',
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.35), 0 2px 8px rgba(59, 130, 246, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15), inset 0 -2px 4px rgba(0, 0, 0, 0.35)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl opacity-90 pointer-events-none"></div>
                <span className="relative z-10">
                  {loading ? 'LOADING...' : (isSignUp ? 'REGISTER' : 'LOGIN')}
                </span>
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-4 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-system-blue/30"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-3 sm:px-4 rounded-full bg-slate-800/60 border border-slate-600/60 text-gray-300 font-rajdhani">OR</span>
              </div>
            </div>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full px-4 py-2.5 sm:px-6 sm:py-3 bg-white text-gray-900 rounded-2xl font-rajdhani font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.2)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)]"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="hidden xs:inline">Continue with Google</span>
              <span className="xs:hidden">Google Sign In</span>
            </button>

            {/* Toggle Sign Up / Sign In */}
            <div className="mt-4 sm:mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setResetEmailSent(false);
                  setResetPasswordLoading(false);
                }}
                className="text-xs sm:text-sm text-gray-400 hover:text-system-blue font-rajdhani transition-colors"
              >
                {isSignUp ? (
                  <>Already have an account? <span className="font-bold">Sign In</span></>
                ) : (
                  <>New hunter? <span className="font-bold">Register Now</span></>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Decorative corners - Hidden on small mobile, visible on larger screens */}
        <div className="hidden xs:block absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-l-2 sm:border-t-4 sm:border-l-4 border-system-blue"></div>
        <div className="hidden xs:block absolute -top-3 -right-3 sm:-top-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-r-2 sm:border-t-4 sm:border-r-4 border-system-blue"></div>
        <div className="hidden xs:block absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-l-2 sm:border-b-4 sm:border-l-4 border-system-blue"></div>
        <div className="hidden xs:block absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-r-2 sm:border-b-4 sm:border-r-4 border-system-blue"></div>
      </div>
    </div>
  );
};

export default AuthPage;



